import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import API from '../backend/api';
import JobSearchStartDateModal from './JobSearchStartDateModal';
import './GmailScraper.css';

const GmailScraper = ({ triggerScan }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [gmailCredentials, setGmailCredentials] = useState(null);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isAutoScanning, setIsAutoScanning] = useState(false);
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  const [profile, setProfile] = useState(null);
  const [hasTriggeredScan, setHasTriggeredScan] = useState(false);

  // Fetch user profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await API.get('/profile/');
        setProfile(response.data);
        
        // Load saved Gmail credentials if they exist
        if (response.data.gmail_credentials) {
          try {
            const parsedCredentials = JSON.parse(response.data.gmail_credentials);
            setGmailCredentials(parsedCredentials);
            console.log('✅ Loaded saved Gmail credentials from profile');
          } catch (err) {
            console.error('Error parsing saved Gmail credentials:', err);
          }
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
      }
    };

    if (user) {
      fetchProfile();
    }
  }, [user]);

  const handleSaveStartDate = async (date) => {
    try {
      const response = await API.put('/profile/', {
        job_search_start_date: date
      });
      setProfile(response.data);
      setShowStartDateModal(false);
      // Proceed with authorization
      proceedWithGmailAuth();
    } catch (err) {
      console.error('Error saving start date:', err);
      throw err;
    }
  };

  const proceedWithGmailAuth = async () => {
    setIsAuthorizing(true);
    setError(null);

    try {
      // Get Gmail OAuth URL
      const response = await API.get('/gmail/oauth/');
      const { authorization_url } = response.data;
      
      // Redirect to OAuth URL (will redirect back to main page)
      window.location.href = authorization_url;
      
    } catch (err) {
      console.error('Gmail authorization error:', err);
      setError(err.response?.data?.error || 'Failed to start Gmail authorization');
      setIsAuthorizing(false);
    }
  };

  const handleGmailAuthorization = async () => {
    if (!user) {
      setError('Please log in first');
      return;
    }

    // Check if user has set job search start date
    if (!profile?.job_search_start_date) {
      // Show modal to set start date first
      setShowStartDateModal(true);
      return;
    }

    // User has start date, proceed with authorization
    proceedWithGmailAuth();
  };

      // Handle OAuth callback when component mounts
      useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');

        if (code && !gmailCredentials) {
          // We have an authorization code, exchange it for credentials
          const exchangeCode = async () => {
            try {
              setIsAuthorizing(true);
              const credResponse = await API.post('/gmail/callback/', {
                code: code
              });

              setGmailCredentials(credResponse.data.credentials);
              setError(null);

              // Clean up URL
              window.history.replaceState({}, document.title, window.location.pathname);
              
              // Automatically trigger Gmail scan after successful authorization
              console.log('Gmail authorization successful, starting automatic scan...');
              
              // Use the credentials directly instead of relying on state
              const autoScan = async () => {
                try {
                  setIsAutoScanning(true);
                  setError(null);
                  setResults(null);

                  const response = await API.post('/gmail/scrape/', {
                    credentials: JSON.stringify(credResponse.data.credentials),
                    max_results: 50
                  });

                  setResults(response.data);
                } catch (err) {
                  console.error('Gmail scraping error:', err);
                  setError(err.response?.data?.error || 'Failed to scrape Gmail');
                } finally {
                  setIsAutoScanning(false);
                }
              };
              
              // Start the scan immediately with the fresh credentials
              autoScan();
              
            } catch (err) {
              console.error('Error exchanging code for credentials:', err);
              setError(err.response?.data?.error || 'Failed to get Gmail credentials');
            } finally {
              setIsAuthorizing(false);
            }
          };

          exchangeCode();
        } else if (error) {
          setError(`Gmail authorization failed: ${error}`);
          setIsAuthorizing(false);
        }
      }, [gmailCredentials, user]);

  const handleGmailScrape = async () => {
    if (!user) {
      setError('Please log in first');
      return;
    }

    if (!gmailCredentials) {
      setError('Please authorize Gmail access first');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await API.post('/gmail/scrape/', {
        credentials: JSON.stringify(gmailCredentials),
        max_results: 50
      });

      setResults(response.data);
    } catch (err) {
      console.error('Gmail scraping error:', err);
      setError(err.response?.data?.error || 'Failed to scrape Gmail');
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-trigger scan when navigated from settings with triggerScan flag
  useEffect(() => {
    if (triggerScan && gmailCredentials && !hasTriggeredScan && !isLoading) {
      console.log('Auto-triggering Gmail scan from settings...');
      setHasTriggeredScan(true);
      handleGmailScrape();
    }
  }, [triggerScan, gmailCredentials, hasTriggeredScan, isLoading]);

  if (!user) {
    return (
      <div className="gmail-scraper">
        <p>Please log in to use Gmail scraping</p>
      </div>
    );
  }

  return (
    <div className="gmail-scraper">
      <div className="scraper-header">
        <h3>Gmail Job Application Scraper</h3>
        <p>Automatically scan your Gmail for job application emails and classify them</p>
        {profile?.gmail_last_sync && (
          <div className="last-sync-info" style={{background: '#e8f5e9', padding: '10px', borderRadius: '5px', marginTop: '10px'}}>
            ✅ Last scanned: <strong>{new Date(profile.gmail_last_sync).toLocaleString()}</strong>
            <br />
            <small>Your applications are saved. Click refresh to check for new emails.</small>
          </div>
        )}
        {profile?.job_search_start_date && (
          <div className="date-range-info">
            📅 Scanning emails from <strong>{new Date(profile.job_search_start_date).toLocaleDateString()}</strong> onwards
            {results?.days_scanned && <span> ({results.days_scanned} days)</span>}
          </div>
        )}
      </div>

          <div className="scraper-controls">
            {!gmailCredentials ? (
              <button
                onClick={handleGmailAuthorization}
                disabled={isAuthorizing}
                className={`authorize-button ${isAuthorizing ? 'loading' : ''}`}
              >
                {isAuthorizing ? 'Authorizing Gmail...' : 'Authorize Gmail Access'}
              </button>
            ) : (
              <div className="authorized-section">
                <div className="authorization-status">
                  <span className="status-icon">✅</span>
                  <span>Gmail access authorized</span>
                </div>
                <button
                  onClick={handleGmailScrape}
                  disabled={isLoading || isAutoScanning}
                  className={`scrape-button ${isLoading || isAutoScanning ? 'loading' : ''}`}
                >
                  {isLoading ? 'Scanning Gmail...' : 
                   isAutoScanning ? 'Auto-scanning Gmail...' : 
                   profile?.gmail_last_sync ? '🔄 Refresh for New Emails' : '📧 Initial Scan - Find My Applications'}
                </button>
                {profile?.gmail_last_sync && (
                  <div style={{marginTop: '8px', fontSize: '0.9em', color: '#666'}}>
                    This will check for new emails since your last scan
                  </div>
                )}
              </div>
            )}
          </div>

          {error && (
            <div className="error-message">
              <h4>Error:</h4>
              <p>{error}</p>
            </div>
          )}

          {(isLoading || isAutoScanning) && (
            <div className="loading-message">
              <p>{isAutoScanning ? 'Auto-scanning Gmail after authorization...' : 'Loading...'}</p>
            </div>
          )}

          {results && (
        <div className="scraper-results">
          <h4>Scan Results:</h4>
          
          {results.stats.total_emails === 0 ? (
            <div className="no-emails-warning">
              <h3>⚠️ No Job Application Emails Found</h3>
              <p>We scanned {results.days_scanned || 90} days of emails but didn't find any job applications.</p>
              
              <div className="suggestions">
                <p><strong>Possible reasons:</strong></p>
                <ul>
                  <li>Your job search start date might be too recent</li>
                  <li>Job emails might be in a different account</li>
                  <li>Emails might be archived or deleted</li>
                  <li>Keywords might not match your email formats</li>
                </ul>
                
                <p><strong>Try this:</strong></p>
                <ul>
                  <li>Go to Settings and adjust your job search start date to an earlier date</li>
                  <li>Check your Gmail account to verify you have job application emails</li>
                  <li>Try scanning again with a broader date range</li>
                </ul>
              </div>
              <div className="manual-add-section">
                <p>You can manually add applications instead:</p>
                <a href="/add-application" className="manual-add-button">
                  Add Application Manually
                </a>
              </div>
            </div>
          ) : (
            <>
              <div className="stats">
                <div className="stat-item">
                  <span className="stat-label">Total Emails:</span>
                  <span className="stat-value">{results.stats.total_emails}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Processed:</span>
                  <span className="stat-value">{results.stats.processed}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">New Applications:</span>
                  <span className="stat-value">{results.stats.new_applications}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Updated Applications:</span>
                  <span className="stat-value">{results.stats.updated_applications}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Errors:</span>
                  <span className="stat-value">{results.stats.errors}</span>
                </div>
              </div>
            </>
          )}

          {results.applications.length > 0 ? (
            <div className="applications-list">
              <h5>Found Job Applications:</h5>
              <div className="applications-grid">
                {results.applications
                  .filter(app => app.classification.status !== 'unknown')
                  .map((app, index) => (
                  <div key={index} className="application-card">
                    <div className="app-header">
                      <span className={`status-badge ${app.classification.status}`}>
                        {app.classification.status}
                      </span>
                      <span className="company">{app.classification.company}</span>
                    </div>
                    <div className="app-details">
                      <p className="position">{app.classification.position}</p>
                      <p className="subject">{app.subject}</p>
                      <p className="sender">From: {app.sender}</p>
                      <p className="date">{new Date(app.date).toLocaleDateString()}</p>
                    </div>
                    <div className="app-note" style={{marginTop: '10px', padding: '8px', background: '#f8f9fa', borderRadius: '4px', fontSize: '0.85em', color: '#666'}}>
                      💡 Go to Dashboard to manage this application
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
      <JobSearchStartDateModal
        isOpen={showStartDateModal}
        onClose={() => setShowStartDateModal(false)}
        onSave={handleSaveStartDate}
        currentDate={profile?.job_search_start_date}
      />
    </div>
  );
};

export default GmailScraper;
